import React, { useMemo } from "react";

import { css, cx } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { Queries } from "@/queries/Queries.ts";
import type { Theme } from "@/theme/CreateTheme.ts";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { OngoingImportSidebar } from "@/uiKit/organisms/PatientSync/OngoingInclusion/OngoingImportSidebar.tsx";
import { OngoingInclusionTable } from "@/uiKit/organisms/PatientSync/OngoingInclusion/OngoingInclusionTable.tsx";

export type OngoingGroupInclusionsCardProps = {
  className?: string;
};

export const OngoingGroupInclusions: React.FC<OngoingGroupInclusionsCardProps> = ({ className }) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const inclusions = Queries.patientSync.useGroupedInclusionDrafts({});
  const completedInclusionIds = useMemo(() => inclusions.filter(i => !!i.phoneNumber).map(i => i.id), [inclusions]);
  const total = inclusions.length;
  const withPhone = completedInclusionIds.length;
  const missingPhone = total - withPhone;

  return (
    <div className={cx(styles.container, className)}>
      <div className={styles.header}>
        <Typography variant="h4">{t("patientSync.ongoingImport.title")}</Typography>
        <Typography variant="body">
          {t("patientSync.ongoingImport.subtitle", { count: total, withPhone, missingPhone })}
        </Typography>
      </div>
      <div className={styles.body}>
        <OngoingInclusionTable inclusions={inclusions} className={styles.table} />
        <OngoingImportSidebar completedInclusionIds={completedInclusionIds} className={styles.sidebar} />
      </div>
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(8),
  }),
  header: css({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  }),
  body: css({
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(8),
    alignItems: "flex-start",
  }),
  table: css({
    backgroundColor: theme.palette.background.paper,
    flex: "2 1",
  }),
  sidebar: css({
    flex: "1 1",
  }),
});
